/**
 * Whether an order's analysis carries a real verdict — pure, no Vue.
 *
 * Why this exists: `order_analysis.is_correct` is DEFAULT false in Postgres and the CV backend
 * inserts the row when the first photo lands, with `app_response` null, long before any
 * comparison runs. The realtime `order_analysis` event for that row therefore reads
 * `isCorrect === false` although nobody has compared anything. `app_response` is written only by
 * `/item-comparison`, so it is what separates a verdict from a freshly created row. Reading
 * `isCorrect === false` alone as "decided wrong" raised a false "revisar" toast and marked the
 * order decided, so the real verdict that followed got no toast.
 *
 * Decided means: the analysis says correct, OR it says wrong AND a comparison has written an
 * app_response, OR the order is the empty-content case, OR staff marked it correct by hand
 * (`manualIsCorrect`, which lives on the inner order and is passed as the second argument).
 */
export const EMPTY_ORDER_CONTENT = 'EMPTY_ORDER_CONTENT';

export function isDecidedAnalysis(analysis, manualIsCorrect = false) {
  if (manualIsCorrect === true) return true;
  if (!analysis) return false;
  if (analysis.isCorrect === true) return true;
  if (analysis.appResponse === EMPTY_ORDER_CONTENT) return true;
  return analysis.isCorrect === false && Boolean(analysis.appResponse);
}

/**
 * Whether an analysis is a DECIDED wrong verdict: `isCorrect === false` AND a comparison has
 * written an app_response (see isDecidedAnalysis). The provisional row the CV inserts with the
 * column default (isCorrect false, appResponse null) is not incorrect, it is "no verdict yet".
 * Use this wherever a surface reads an order_analysis as "incorrect" (tile colour, help flow).
 */
export function isIncorrectAnalysis(analysis) {
  return analysis?.isCorrect === false && isDecidedAnalysis(analysis);
}
